/**
 * Live training sessions — an HR-scheduled meeting (Zoho Meeting, or a pasted link) that trainees
 * join through a People Orbit link rather than the raw provider link. That indirection is the whole
 * point: every click is tied to a named person, and once the session ends the provider's
 * participant report is pulled back and turned into a per-person attendance record that outlives
 * the meeting (history, regular-absentee flags, manager alerts).
 *
 * Not to be confused with `trainingSessionsContract` (training.contract.ts), which is one
 * self-paced working stint on an assignment.
 *
 * TWO JOIN LINKS:
 *   - Per-person (`/j/:token`, sent in invites): the token alone identifies the invitee, no sign-in.
 *     Resolved by `publicTrainingJoinContract.resolve`.
 *   - Shareable (`shareUrl`, for "Copy link" into a WhatsApp group): requires sign-in, then
 *     `trainingLiveSessionsContract.join`. A signed-in person who was not invited is added as a
 *     walk-in when the session allows it (`allowWalkIns` — an "open" session).
 *
 * WHO IS INVITED (`audience`): `selected` = the people picked in `inviteePersonIds`;
 * `organisation` = every active employee within the scheduler's scope, expanded server-side into
 * one invitee row each (own link, own reminders), exactly as if each had been picked. People who
 * join the organisation later are NOT added automatically. `audience` and `allowWalkIns` combine:
 * an organisation-wide session can also be open to anyone signed in who has the link.
 *
 * Contract-only per invariant 8: these routes land before the handlers that implement them.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  paginatedSchema,
  paginationQuerySchema,
  timezoneSchema,
  trainingLiveAttendanceSourceSchema,
  trainingLiveAttendanceStatusSchema,
  trainingLiveAttendanceSyncStatusSchema,
  trainingLiveSessionAudienceSchema,
  trainingLiveSessionProviderSchema,
  trainingLiveSessionStatusSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

const wallClockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Must be 24-hour HH:MM');

const managePermission = ['learning:manage', 'learning.assign:execute'] as const;

/** Stored status plus the time-derived `live`/`ended` — computed server-side, never stored. */
export const trainingLiveSessionDisplayStatusSchema = z.enum([
  'scheduled',
  'live',
  'ended',
  'cancelled',
  'completed',
]);
export type TrainingLiveSessionDisplayStatus = z.infer<typeof trainingLiveSessionDisplayStatusSchema>;

/** A person is flagged a regular absentee when they missed at least `minAbsences` of their last
 *  `window` live sessions (excused sessions are skipped, not counted as either). */
export const TRAINING_LIVE_ABSENTEE_RULE = { window: 5, minAbsences: 3 } as const;

export const trainingLiveAttendanceCountsSchema = z.object({
  invited: z.number().int(),
  clicked: z.number().int(),
  present: z.number().int(),
  late: z.number().int(),
  leftEarly: z.number().int(),
  absent: z.number().int(),
  excused: z.number().int(),
  pending: z.number().int(),
});
export type TrainingLiveAttendanceCounts = z.infer<typeof trainingLiveAttendanceCountsSchema>;

export const trainingLiveSessionSummarySchema = z.object({
  id: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  taskId: uuidSchema.nullable(),
  taskTitle: z.string().nullable(),
  provider: trainingLiveSessionProviderSchema,
  status: trainingLiveSessionStatusSchema,
  displayStatus: trainingLiveSessionDisplayStatusSchema,
  startsAt: instantSchema,
  endsAt: instantSchema,
  /** IANA zone the session was scheduled in — the UI shows times in it (invariant 3). */
  timezone: z.string(),
  durationMinutes: z.number().int(),
  hostPersonId: uuidSchema,
  hostName: z.string(),
  /** Who was invited: picked people, or everyone in the organisation (within the scheduler's
   *  scope) at the time it was scheduled. */
  audience: trainingLiveSessionAudienceSchema,
  /** "Open" session: anyone signed in to the organisation who opens `shareUrl` can join; they are
   *  added as a walk-in invitee on their first click. False = invitees only. */
  allowWalkIns: z.boolean(),
  counts: trainingLiveAttendanceCountsSchema,
  attendanceSyncStatus: trainingLiveAttendanceSyncStatusSchema,
  attendanceSyncedAt: instantSchema.nullable(),
  /** The People Orbit shareable link (sign-in required) — what "Copy link" copies. Never the raw
   *  provider link. */
  shareUrl: z.string().url(),
  createdAt: instantSchema,
});
export type TrainingLiveSessionSummary = z.infer<typeof trainingLiveSessionSummarySchema>;

export const trainingLiveInviteeSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  departmentName: z.string().nullable(),
  addedVia: z.enum(['invite', 'walk_in']),
  invitedAt: instantSchema,
  lastNotifiedAt: instantSchema.nullable(),
  firstClickedAt: instantSchema.nullable(),
  clickCount: z.number().int(),
  joinedAt: instantSchema.nullable(),
  leftAt: instantSchema.nullable(),
  minutesAttended: z.number().int().nullable(),
  /** Of the scheduled duration, 0-100. */
  attendancePercent: z.number().min(0).max(100).nullable(),
  attendanceStatus: trainingLiveAttendanceStatusSchema,
  attendanceSource: trainingLiveAttendanceSourceSchema.nullable(),
  overrideNote: z.string().nullable(),
  overriddenByName: z.string().nullable(),
});
export type TrainingLiveInvitee = z.infer<typeof trainingLiveInviteeSchema>;

export const trainingLiveSessionDetailSchema = trainingLiveSessionSummarySchema.extend({
  /** Zoho's host start link — only for the host and `learning:manage`; null for everyone else and
   *  for `manual` sessions. */
  hostStartUrl: z.string().url().nullable(),
  lateAfterMinutes: z.number().int(),
  minAttendancePercent: z.number().int(),
  reminderOffsetsMinutes: z.array(z.number().int()),
  attendanceSyncError: z.string().nullable(),
  cancelledAt: instantSchema.nullable(),
  cancelReason: z.string().nullable(),
  invitees: z.array(trainingLiveInviteeSchema),
});
export type TrainingLiveSessionDetail = z.infer<typeof trainingLiveSessionDetailSchema>;

/** The employee's own view of a session they are invited to. */
export const myTrainingLiveSessionSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  provider: trainingLiveSessionProviderSchema,
  displayStatus: trainingLiveSessionDisplayStatusSchema,
  startsAt: instantSchema,
  endsAt: instantSchema,
  timezone: z.string(),
  hostName: z.string(),
  attendanceStatus: trainingLiveAttendanceStatusSchema,
  /** True from 15 minutes before the start until the end. */
  canJoin: z.boolean(),
});
export type MyTrainingLiveSession = z.infer<typeof myTrainingLiveSessionSchema>;

const reminderOffsetsSchema = z
  .array(z.number().int().min(5).max(10_080))
  .max(3)
  .refine((v) => new Set(v).size === v.length, 'Duplicate reminder');

const sessionFieldsSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(5000).optional(),
  /** Optional link to a library course the live session teaches. */
  taskId: uuidSchema.optional(),
  /** Local wall-clock date and time in `timezone` — resolved to UTC on the server (invariant 3). */
  date: dateOnlySchema,
  startTime: wallClockTimeSchema,
  durationMinutes: z.number().int().min(15).max(480),
  timezone: timezoneSchema,
  /** `manual` only: the meeting link HR pasted. https only. */
  externalJoinUrl: z.string().url().startsWith('https://').max(2000).optional(),
  reminderOffsetsMinutes: reminderOffsetsSchema.default([1440, 15]),
  /** Joined more than this many minutes after the start → `late`. */
  lateAfterMinutes: z.number().int().min(0).max(120).default(10),
  /** Attended less than this share of the scheduled duration → `left_early`. */
  minAttendancePercent: z.number().int().min(1).max(100).default(75),
  /** `selected` (default) invites `inviteePersonIds`; `organisation` invites every active employee
   *  within the caller's scope. On update, switching to `organisation` invites everyone not yet
   *  invited; switching back to `selected` keeps the existing invitees. */
  audience: trainingLiveSessionAudienceSchema.default('selected'),
  /** "Anyone in the organisation with the link can join": a signed-in person who opens the shared
   *  link without an invitation is added as a walk-in. Off = invitees only. */
  allowWalkIns: z.boolean().default(false),
});

/**
 * Departments / locations whose current active members are invited — expanded server-side into one
 * invitee row each (own link, own reminders), within the scheduler's scope, host left out. A
 * snapshot like `organisation`: people who move in later are not added.
 */
const inviteGroupIdsSchema = z.array(uuidSchema).max(100).default([]);

export const createTrainingLiveSessionBodySchema = sessionFieldsSchema
  .extend({
    provider: trainingLiveSessionProviderSchema,
    /** Defaults to the caller. With `zoho_meeting` the meeting is created on the connected Zoho
     *  account regardless; the host is who People Orbit shows as the trainer. */
    hostPersonId: uuidSchema.optional(),
    /** With `departmentIds` / `locationIds`, at least one is required only when `audience` is
     *  `selected` and `allowWalkIns` is off. Ignored when `audience` is `organisation` — everyone
     *  in scope is invited. */
    inviteePersonIds: z.array(uuidSchema).max(1000).default([]),
    departmentIds: inviteGroupIdsSchema,
    locationIds: inviteGroupIdsSchema,
    sendInvites: z.boolean().default(true),
  })
  .superRefine((body, ctx) => {
    const picked = body.inviteePersonIds.length + body.departmentIds.length + body.locationIds.length;
    if (body.audience === 'selected' && !body.allowWalkIns && picked === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['inviteePersonIds'],
        message: 'Invite people, a department or a location, invite everyone, or let anyone with the link join',
      });
    }
    if (body.provider === 'manual' && !body.externalJoinUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['externalJoinUrl'],
        message: 'Paste the meeting link for a manual session',
      });
    }
    if (body.provider === 'zoho_meeting' && body.externalJoinUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['externalJoinUrl'],
        message: 'Zoho creates the link — leave this empty',
      });
    }
  });
export type CreateTrainingLiveSessionBody = z.infer<typeof createTrainingLiveSessionBodySchema>;

export const updateTrainingLiveSessionBodySchema = sessionFieldsSchema
  .omit({ taskId: true })
  .partial()
  .extend({
    /** `null` (or an empty string) clears it; omitted leaves it unchanged. */
    description: z.string().trim().max(5000).nullable().optional(),
    /** Tell invitees about a changed time or link. Default on. */
    notifyInvitees: z.boolean().default(true),
  });
export type UpdateTrainingLiveSessionBody = z.infer<typeof updateTrainingLiveSessionBodySchema>;

export const cancelTrainingLiveSessionBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
  notifyInvitees: z.boolean().default(true),
});

export const addTrainingLiveInviteesBodySchema = z
  .object({
    personIds: z.array(uuidSchema).max(1000).default([]),
    /** Everyone currently in these departments / locations who is not invited yet. Unlike a
     *  person picked by name, a withdrawn invitation found this way stays withdrawn. */
    departmentIds: inviteGroupIdsSchema,
    locationIds: inviteGroupIdsSchema,
    sendInvites: z.boolean().default(true),
  })
  .refine((body) => body.personIds.length + body.departmentIds.length + body.locationIds.length > 0, {
    path: ['personIds'],
    message: 'Pick at least one person, department or location',
  });

export const resendTrainingLiveInvitesBodySchema = z.object({
  /** Omit for everyone still invited. */
  personIds: z.array(uuidSchema).max(1000).optional(),
  /** Only those who have not clicked their link yet. */
  onlyNotClicked: z.boolean().default(false),
});

export const markTrainingLiveAttendanceBodySchema = z.object({
  entries: z
    .array(
      z.object({
        personId: uuidSchema,
        status: trainingLiveAttendanceStatusSchema.exclude(['pending']),
        /** Required — every override is audited with its reason. */
        note: z.string().trim().min(1).max(500),
      }),
    )
    .min(1)
    .max(1000),
});

export const listTrainingLiveSessionsQuerySchema = paginationQuerySchema.extend({
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  status: trainingLiveSessionDisplayStatusSchema.optional(),
  hostPersonId: uuidSchema.optional(),
  taskId: uuidSchema.optional(),
});

export const listMyTrainingLiveSessionsQuerySchema = paginationQuerySchema.extend({
  when: z.enum(['upcoming', 'past']).default('upcoming'),
});

/** One shape for both join routes so the landing page renders every outcome the same way. */
export const trainingLiveJoinResponseSchema = z.object({
  state: z.enum(['open', 'not_open_yet', 'ended', 'cancelled']),
  /** Only when `state = 'open'`. The provider link the browser navigates to next. */
  joinUrl: z.string().url().nullable(),
  /** When the link opens (15 minutes before the start). */
  opensAt: instantSchema,
  sessionId: uuidSchema,
  title: z.string(),
  startsAt: instantSchema,
  endsAt: instantSchema,
  timezone: z.string(),
  hostName: z.string(),
  /** Which provider runs the meeting, so the landing page knows whether it can offer Zoho's own
   *  "Join as <email>" and embedded join box (`zoho_meeting`) or only a plain "Join the meeting"
   *  button (`manual`: Meet, Teams, a pasted link). */
  provider: trainingLiveSessionProviderSchema,
  /** The invitee's own (or, on the signed-in route, the caller's own) work email, used to prefill
   *  the name Zoho shows and matches attendance on. Null when the person has none on file. Only
   *  meaningful when `state` is `open`; null otherwise. Personal data: never logged. */
  attendeeEmail: z.string().nullable(),
});
export type TrainingLiveJoinResponse = z.infer<typeof trainingLiveJoinResponseSchema>;

export const trainingLiveSyncResponseSchema = z.object({
  attendanceSyncStatus: trainingLiveAttendanceSyncStatusSchema,
  attendanceSyncedAt: instantSchema.nullable(),
  attendanceSyncError: z.string().nullable(),
  counts: trainingLiveAttendanceCountsSchema,
});

export const trainingLiveHistoryQuerySchema = paginationQuerySchema.extend({
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});

export const trainingLiveHistoryItemSchema = z.object({
  sessionId: uuidSchema,
  title: z.string(),
  startsAt: instantSchema,
  timezone: z.string(),
  hostName: z.string(),
  displayStatus: trainingLiveSessionDisplayStatusSchema,
  attendanceStatus: trainingLiveAttendanceStatusSchema,
  minutesAttended: z.number().int().nullable(),
  attendancePercent: z.number().min(0).max(100).nullable(),
  firstClickedAt: instantSchema.nullable(),
});

export const trainingLivePersonStatsSchema = z.object({
  invited: z.number().int(),
  attended: z.number().int(),
  absent: z.number().int(),
  excused: z.number().int(),
  /** attended / (invited − excused − pending), 0-100; null when nothing is countable yet. */
  attendanceRatePercent: z.number().min(0).max(100).nullable(),
  /** Consecutive absences counting back from the most recent finalised session. */
  currentAbsenceStreak: z.number().int(),
  flagged: z.boolean(),
});

export const trainingLiveHistoryResponseSchema = paginatedSchema(trainingLiveHistoryItemSchema).extend({
  person: z.object({ personId: uuidSchema, personName: z.string() }),
  stats: trainingLivePersonStatsSchema,
});

export const trainingLiveAttendanceReportQuerySchema = paginationQuerySchema.extend({
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  legalEntityId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  flaggedOnly: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});

export const trainingLiveAttendanceReportRowSchema = trainingLivePersonStatsSchema.extend({
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  departmentName: z.string().nullable(),
  managerName: z.string().nullable(),
  present: z.number().int(),
  late: z.number().int(),
  leftEarly: z.number().int(),
  lastAbsentAt: instantSchema.nullable(),
});
export type TrainingLiveAttendanceReportRow = z.infer<typeof trainingLiveAttendanceReportRowSchema>;

export const trainingLiveAttendanceReportResponseSchema = paginatedSchema(
  trainingLiveAttendanceReportRowSchema,
).extend({
  kpis: z.object({
    sessions: z.number().int(),
    invited: z.number().int(),
    avgAttendanceRatePercent: z.number().min(0).max(100).nullable(),
    flaggedCount: z.number().int(),
  }),
});

export const trainingLiveSessionsContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      query: listTrainingLiveSessionsQuerySchema,
      responses: {
        200: paginatedSchema(trainingLiveSessionSummarySchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Live training sessions (instructor-led classes over Zoho Meeting or a pasted meeting '
        + 'link), newest first, scope-filtered to the people the caller manages',
      metadata: routeMeta(managePermission),
    },
    listMine: {
      method: 'GET',
      path: '/mine',
      query: listMyTrainingLiveSessionsQuerySchema,
      responses: { 200: paginatedSchema(myTrainingLiveSessionSchema), 401: errorBodySchema },
      summary:
        "The caller's own live training sessions — upcoming (soonest first) or past (newest "
        + 'first)',
      metadata: routeMeta('learning:read'),
    },
    attendanceReport: {
      method: 'GET',
      path: '/reports/attendance',
      query: trainingLiveAttendanceReportQuerySchema,
      responses: {
        200: trainingLiveAttendanceReportResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Live training session attendance per person: rates, absence streaks and '
        + 'regular-absentee flags',
      metadata: routeMeta(managePermission),
    },
    personHistory: {
      method: 'GET',
      path: '/people/:personId/history',
      query: trainingLiveHistoryQuerySchema,
      responses: {
        200: trainingLiveHistoryResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "One person's live training session history and stats — own, or a person the caller "
        + 'manages',
      metadata: routeMeta('learning:read'),
    },
    create: {
      method: 'POST',
      path: '/',
      headers: idempotencyHeadersSchema,
      body: createTrainingLiveSessionBodySchema,
      responses: {
        201: trainingLiveSessionDetailSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        503: errorBodySchema,
      },
      summary:
        'Schedule a live training session: date, start time, duration, timezone, optional '
        + 'course (taskId); provider zoho_meeting creates the meeting, manual stores a pasted '
        + 'https link. Invite people, departments, locations or the whole organisation and send '
        + 'invites',
      metadata: routeMeta(managePermission),
    },
    get: {
      method: 'GET',
      path: '/:sessionId',
      responses: {
        200: trainingLiveSessionDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One live training session with every invitee and their click and attendance record',
      metadata: routeMeta(managePermission),
    },
    update: {
      method: 'PATCH',
      path: '/:sessionId',
      body: updateTrainingLiveSessionBodySchema,
      responses: {
        200: trainingLiveSessionDetailSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        503: errorBodySchema,
      },
      summary:
        'Edit or reschedule a live training session (updates the Zoho meeting) and optionally '
        + 'notify invitees',
      metadata: routeMeta(managePermission),
    },
    cancel: {
      method: 'POST',
      path: '/:sessionId/cancel',
      body: cancelTrainingLiveSessionBodySchema,
      responses: {
        200: trainingLiveSessionDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Cancel a live training session (deletes the Zoho meeting best-effort) and optionally '
        + 'notify invitees',
      metadata: routeMeta(managePermission),
    },
    addInvitees: {
      method: 'POST',
      path: '/:sessionId/invitees',
      headers: idempotencyHeadersSchema,
      body: addTrainingLiveInviteesBodySchema,
      responses: {
        200: trainingLiveSessionDetailSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Invite more people, departments or locations to a live training session; people '
        + 'already invited are skipped',
      metadata: routeMeta(managePermission),
    },
    removeInvitee: {
      method: 'POST',
      path: '/:sessionId/invitees/:personId/remove',
      body: z.object({}),
      responses: {
        200: trainingLiveSessionDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        "Withdraw one person's invitation to a live training session (kept as history; their "
        + 'link stops working)',
      metadata: routeMeta(managePermission),
    },
    resendInvites: {
      method: 'POST',
      path: '/:sessionId/resend',
      headers: idempotencyHeadersSchema,
      body: resendTrainingLiveInvitesBodySchema,
      responses: {
        200: z.object({ notifiedCount: z.number().int() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Re-send live training session invitations (same personal links) to everyone or a '
        + 'subset',
      metadata: routeMeta(managePermission),
    },
    join: {
      method: 'POST',
      path: '/:sessionId/join',
      body: z.object({}),
      responses: {
        200: trainingLiveJoinResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Join a live training session as the signed-in person: records the click and returns '
        + 'the meeting link',
      metadata: routeMeta('learning:read'),
    },
    syncAttendance: {
      method: 'POST',
      path: '/:sessionId/attendance/sync',
      body: z.object({}),
      responses: {
        200: trainingLiveSyncResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        503: errorBodySchema,
      },
      summary:
        "Pull the meeting provider's participant report for a live training session now, "
        + 'instead of waiting for the scheduled sync',
      metadata: routeMeta(managePermission),
    },
    markAttendance: {
      method: 'POST',
      path: '/:sessionId/attendance',
      body: markTrainingLiveAttendanceBodySchema,
      responses: {
        200: trainingLiveSessionDetailSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Set live training session attendance by hand (manual sessions, or overriding the '
        + 'report) — note required',
      metadata: routeMeta(managePermission),
    },
  },
  { pathPrefix: '/training/live-sessions' },
);

/**
 * Genuinely public (`routeMeta('public')`): the personal link in an invite works without signing
 * in — the 256-bit token is the credential, and all it grants is "record that this invitee clicked,
 * and tell them where the meeting is". Rate-limited at the adapter.
 */
export const publicTrainingJoinContract = c.router(
  {
    resolve: {
      method: 'POST',
      path: '/:token',
      body: z.object({}),
      responses: {
        200: trainingLiveJoinResponseSchema,
        404: errorBodySchema,
        429: errorBodySchema,
      },
      summary: 'Resolve a personal join link: records the click, returns the meeting link',
      metadata: routeMeta('public', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/public/training-join' },
);
